import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDispatch } from '../../Redux/CustomHooks';
import {
  fetchActiveStore,
  openStore,
  closeStore,
  startDiningTable,
  moveDiningTable,
  fetchBill,
  acknowledgeCheckoutRequest,
  cancelCheckoutRequest,
} from '../../Redux/ActiveStore/activeStoreApi';
import { ActiveDiningTable } from '../../Redux/ActiveStore/activeStoreSlice';
import { RootState } from '../../Redux/Store';
import BillConfirmModal from './BillConfirmModal';
import TableMoveDialog from './TableMoveDialog';
import NumberInput from '../../Components/NumberInput/NumberInput';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';
import {
  Box,
  Button,
  Container,
  Typography,
  List,
  ListItem,
  Paper,
  Stack,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  keyframes,
} from '@mui/material';
import {
  StorefrontOutlined as StorefrontIcon,
  AccessTimeOutlined as TimeIcon,
  RestaurantOutlined as RestaurantIcon,
  PointOfSaleOutlined as PosIcon,
  PeopleAltOutlined as PeopleIcon,
  SwapHorizOutlined as SwapIcon,
  ViewListOutlined as ViewListIcon,
  GridViewOutlined as GridViewIcon,
  ReceiptLongOutlined as ReceiptIcon,
  CheckCircleOutline as CheckCircleIcon,
} from '@mui/icons-material';

/** テーブル一覧の表示モード（ADR 0066）。localStorage に永続化する。 */
type TableViewMode = 'list' | 'grid';
const TABLE_VIEW_MODE_KEY = 'storeManagementTableViewMode';

/** 会計リクエスト（REQUESTED）の Chip を控えめに点滅させる（ADR 0068）。prefers-reduced-motion では止める。 */
const checkoutPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;
const checkoutPulseSx = {
  animation: `${checkoutPulse} 1.6s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

/**
 * thunk の reject payload（ApiError.message = BE の {status,message,...} JSON 文字列）からユーザー向け message を取り出す。
 * JSON でなければそのまま、空なら fallback。
 */
const toErrorMessage = (payload: unknown, fallback: string): string => {
  if (typeof payload !== 'string' || !payload) return fallback;
  try {
    const parsed = JSON.parse(payload);
    if (parsed && typeof parsed.message === 'string' && parsed.message) {
      return parsed.message;
    }
  } catch {
    // JSON でない本文はそのまま表示する
  }
  return payload;
};

/** 会計リクエスト状態の Chip（グリッド・リスト共通）。NONE / 未取得なら null。 */
const CheckoutRequestChip: React.FC<{ table: ActiveDiningTable }> = ({ table }) => {
  const checkoutRequest = table.checkoutRequest;
  if (!checkoutRequest || checkoutRequest.status === 'NONE') return null;
  const method = checkoutRequest.paymentMethod ? `・${checkoutRequest.paymentMethod}` : '';
  if (checkoutRequest.status === 'REQUESTED') {
    return (
      <Chip
        size="small"
        color="warning"
        icon={<ReceiptIcon />}
        label={`会計リクエスト${method}`}
        sx={{ fontWeight: 'bold', maxWidth: '100%', ...checkoutPulseSx }}
      />
    );
  }
  return (
    <Chip
      size="small"
      color="success"
      variant="outlined"
      icon={<CheckCircleIcon />}
      label={`会計受付済み${method}`}
      sx={{ fontWeight: 'bold', maxWidth: '100%', bgcolor: 'background.paper' }}
    />
  );
};

const StoreManagement: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeStore = useSelector((state: RootState) => state.activeStore.store);
  const clerk_id = useSelector(
    (state: RootState) => state.company.stores[0]?.clerks[0]?.id
  );
  const [error, setError] = useState<string | null>(null);
  const [visitors, setVisitors] = useState<{ [key: string]: number }>({});
  const [billData, setBillData] = useState<any>(null);
  const [isBillModalOpen, setIsBillModalOpen] = useState(false);
  const [selectedDiningTableId, setSelectedDiningTableId] = useState<string | null>(null);
  const [moveSource, setMoveSource] = useState<{ activeDiningTableId: string; tableName: string } | null>(null);
  // 会計リクエスト取消の確認ダイアログのターゲット（ADR 0068）
  const [cancelTarget, setCancelTarget] = useState<{ activeDiningTableId: string; tableName: string } | null>(null);
  // テーブル一覧の表示モード（ADR 0066）。localStorage から復元し、切替時に保存する。
  const [viewMode, setViewMode] = useState<TableViewMode>(() =>
    localStorage.getItem(TABLE_VIEW_MODE_KEY) === 'grid' ? 'grid' : 'list'
  );
  // グリッドモードで「開始」をタップした非稼働テーブル（人数選択ダイアログのターゲット）。
  const [startTarget, setStartTarget] = useState<{ id: string; name: string } | null>(null);
  const [startVisitors, setStartVisitors] = useState(1);

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, next: TableViewMode | null) => {
    if (!next) return;
    setViewMode(next);
    localStorage.setItem(TABLE_VIEW_MODE_KEY, next);
  };

  useEffect(() => {
    if (store_id) dispatch(fetchActiveStore(store_id));
  }, [dispatch, store_id]);

  const handleOpenStore = () => {
    if (!store_id) return;
    dispatch(openStore(store_id)).catch((err) => setError(err.message));
  };

  const handleCloseStore = () => {
    if (!store_id) return;
    if (activeStore?.activeTables.length) {
      setError('稼働中のテーブルがあります');
      return;
    }
    dispatch(closeStore(store_id)).catch((err) => setError(err.message));
  };

  const handleVisitorChange = (diningTableId: string, value: number) => {
    setVisitors((prev) => ({ ...prev, [diningTableId]: value }));
  };

  const handleStartDiningTable = (activeStoreId: string, diningTableId: string) => {
    if (!store_id) return;
    // NumberInput は defaultValue=1 を表示するが、未操作だと state が undefined のため
    // 0 名で開始されるバグがあった。表示と同じ 1 を既定値にする。
    const numberOfVisitors = visitors[diningTableId] ?? 1;
    dispatch(
      startDiningTable({
        storeId: store_id,
        activeStoreId,
        diningTableId,
        numberOfVisitors,
      })
    )
      .then(() => {
        dispatch(fetchActiveStore(store_id));
      })
      .catch((err) => setError(err.message));
  };

  const handleBilling = (activeStoreId: string, activeDiningTableId: string) => {
    setSelectedDiningTableId(activeDiningTableId);
    dispatch(fetchBill({ activeStoreId, activeDiningTableId })).then((action) => {
      if (fetchBill.fulfilled.match(action)) {
        setBillData(action.payload);
        setIsBillModalOpen(true);
      } else {
        setError(action.payload as string);
      }
    });
  };

  const handleMoveDiningTable = (toDiningTableId: string) => {
    if (!store_id || !activeStore || !moveSource) return;
    dispatch(
      moveDiningTable({
        storeId: store_id,
        activeStoreId: activeStore.activeStoreId,
        activeDiningTableId: moveSource.activeDiningTableId,
        toDiningTableId,
      })
    ).then((action) => {
      if (moveDiningTable.rejected.match(action)) {
        const message = (action.payload as string) ?? '';
        setError(
          message.includes('409')
            ? '移動先のテーブルは稼働中のため移動できません'
            : `テーブル移動に失敗しました: ${message}`
        );
      }
    });
    setMoveSource(null);
  };

  // 会計リクエストの受領（ADR 0068）。成功・失敗にかかわらず再取得して他端末の操作とも整合させる。
  const handleAcknowledgeCheckoutRequest = (activeStoreId: string, activeDiningTableId: string) => {
    if (!store_id) return;
    dispatch(acknowledgeCheckoutRequest({ activeStoreId, activeDiningTableId })).then((action) => {
      if (acknowledgeCheckoutRequest.rejected.match(action)) {
        setError(toErrorMessage(action.payload, '会計リクエストの受領に失敗しました'));
      }
      dispatch(fetchActiveStore(store_id));
    });
  };

  // 会計リクエストの取消（確認ダイアログの確定）。REQUESTED / ACKNOWLEDGED どちらからも NONE に戻す。
  const handleConfirmCancelCheckoutRequest = () => {
    if (!store_id || !activeStore || !cancelTarget) return;
    dispatch(
      cancelCheckoutRequest({
        activeStoreId: activeStore.activeStoreId,
        activeDiningTableId: cancelTarget.activeDiningTableId,
      })
    ).then((action) => {
      if (cancelCheckoutRequest.rejected.match(action)) {
        setError(toErrorMessage(action.payload, '会計リクエストの取消に失敗しました'));
      }
      dispatch(fetchActiveStore(store_id));
    });
    setCancelTarget(null);
  };

  const handleClerkOrder = (activeStoreId: string, activeDiningTableId: string) => {
    if (!clerk_id) return;
    const url = `/public/active/${activeStoreId}/${activeDiningTableId}/order/by-clerk/${clerk_id}`;
    window.open(url, '_blank');
  };

  // グリッドモードの人数選択ダイアログから開始する（ADR 0066）。
  const handleStartFromDialog = () => {
    if (!activeStore || !startTarget) return;
    handleVisitorChange(startTarget.id, startVisitors);
    if (!store_id) return;
    dispatch(
      startDiningTable({
        storeId: store_id,
        activeStoreId: activeStore.activeStoreId,
        diningTableId: startTarget.id,
        numberOfVisitors: startVisitors,
      })
    )
      .then(() => {
        dispatch(fetchActiveStore(store_id));
      })
      .catch((err) => setError(err.message));
    setStartTarget(null);
  };

  // 「2026/07/04 09:01」形式。以前の「YYYY年MM月DD日 HH時mm分ss秒」はモバイルで折り返して読みにくかった。
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const padZero = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}/${padZero(date.getMonth() + 1)}/${padZero(date.getDate())} ${padZero(
      date.getHours()
    )}:${padZero(date.getMinutes())}`;
  };

  return (
    <Container maxWidth={false}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {activeStore && (
              <Chip
                icon={<StorefrontIcon />}
                label="開店中"
                color="success"
                variant="filled"
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {!activeStore ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h5" gutterBottom>
                閉店中
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<StorefrontIcon />}
                onClick={handleOpenStore}
              >
                開店
              </Button>
            </Box>
          ) : (
            <Stack spacing={4}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  borderRadius: 2,
                }}
              >
                <TimeIcon color="action" fontSize="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    開店時間
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    {formatDate(activeStore.startTime)}
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleCloseStore}
                    startIcon={<StorefrontIcon />}
                  >
                    閉店
                  </Button>
                </Box>
              </Paper>

              {/* 表示モード切替（ADR 0066）。リスト（従来）とグリッド（統合タイル）。 */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -2 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={viewMode}
                  onChange={handleViewModeChange}
                  aria-label="テーブルの表示モード"
                >
                  <ToggleButton value="list" aria-label="リスト表示">
                    <ViewListIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="grid" aria-label="グリッド表示">
                    <GridViewIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {viewMode === 'grid' ? (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridViewIcon />
                  テーブル
                </Typography>
                {activeStore.activeTables.length + activeStore.inActiveTables.length === 0 ? (
                  <Alert severity="info">テーブルがありません</Alert>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                      gap: 1.5,
                    }}
                  >
                    {/* 稼働中タイル: グリーン背景・操作はアイコンのみ（店員オーダー / 会計）。会計リクエスト中は枠を warning にして受領を主操作にする（ADR 0068） */}
                    {activeStore.activeTables.map((table) => {
                      const checkoutStatus = table.checkoutRequest?.status ?? 'NONE';
                      const isRequested = checkoutStatus === 'REQUESTED';
                      const isAcknowledged = checkoutStatus === 'ACKNOWLEDGED';
                      return (
                      <Paper
                        key={table.id}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          minHeight: 116,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          bgcolor: 'rgba(76, 175, 80, 0.14)',
                          borderColor: isRequested ? 'warning.main' : 'rgba(76, 175, 80, 0.45)',
                          borderWidth: isRequested ? 2 : 1,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                          {table.diningTable.name}
                        </Typography>
                        <CheckoutRequestChip table={table} />
                        {table.numberOfVisitors != null && (
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={<PeopleIcon />}
                            label={`${table.numberOfVisitors} 名`}
                            sx={{ alignSelf: 'flex-start' }}
                          />
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                          <Tooltip title="店員オーダー">
                            <IconButton
                              color="info"
                              size="small"
                              aria-label={`${table.diningTable.name} の店員オーダー`}
                              onClick={() => handleClerkOrder(activeStore.activeStoreId, table.id)}
                              sx={{ flex: 1, border: 1, borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper' }}
                            >
                              <RestaurantIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isRequested && (
                            <Button
                              variant="contained"
                              color="warning"
                              size="small"
                              aria-label={`${table.diningTable.name} の会計リクエストを受領`}
                              onClick={() => handleAcknowledgeCheckoutRequest(activeStore.activeStoreId, table.id)}
                              sx={{ flex: 2, minWidth: 0, px: 1, whiteSpace: 'nowrap' }}
                            >
                              受領
                            </Button>
                          )}
                          <Tooltip title="会計">
                            <IconButton
                              color={isAcknowledged ? 'primary' : 'warning'}
                              size="small"
                              aria-label={`${table.diningTable.name} の会計`}
                              onClick={() => handleBilling(activeStore.activeStoreId, table.id)}
                              sx={
                                isAcknowledged
                                  ? {
                                      // 会計受付済みは会計が主操作: contained primary 相当の見せ方にする
                                      flex: 1,
                                      borderRadius: 1.5,
                                      bgcolor: 'primary.main',
                                      color: 'common.white',
                                      '&:hover': { bgcolor: 'primary.dark' },
                                    }
                                  : { flex: 1, border: 1, borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper' }
                              }
                            >
                              <PosIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        {isAcknowledged && (
                          <Button
                            size="small"
                            variant="text"
                            color="inherit"
                            onClick={() => setCancelTarget({ activeDiningTableId: table.id, tableName: table.diningTable.name })}
                            sx={{ alignSelf: 'flex-end', minWidth: 0, px: 0.5, py: 0, fontSize: '0.7rem', color: 'text.secondary' }}
                          >
                            取消
                          </Button>
                        )}
                      </Paper>
                      );
                    })}
                    {/* 非稼働中タイル: グレー背景・開始ボタンのみ（人数はダイアログで選択） */}
                    {activeStore.inActiveTables.map((table) => (
                      <Paper
                        key={table.diningTable.id}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          minHeight: 116,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" noWrap>
                          {table.diningTable.name}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          sx={{ mt: 'auto' }}
                          onClick={() => {
                            setStartVisitors(1);
                            setStartTarget({ id: table.diningTable.id, name: table.diningTable.name });
                          }}
                        >
                          開始
                        </Button>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
              ) : (
              <>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RestaurantIcon />
                  稼働中テーブル
                </Typography>

                {activeStore.activeTables.length ? (
                  <List sx={{ p: 0 }}>
                    {activeStore.activeTables.map((table) => {
                      const checkoutStatus = table.checkoutRequest?.status ?? 'NONE';
                      const isRequested = checkoutStatus === 'REQUESTED';
                      const isAcknowledged = checkoutStatus === 'ACKNOWLEDGED';
                      return (
                      <ListItem
                        key={table.id}
                        sx={{
                          display: 'block',
                          p: 2,
                          mb: 2,
                          border: isRequested ? 2 : 1,
                          // 会計リクエスト中は枠を warning にして一覧で気づけるようにする（ADR 0068）
                          borderColor: isRequested ? 'warning.main' : 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Stack spacing={2}>
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                              <Typography variant="subtitle1" color="primary" fontWeight="bold">
                                {table.diningTable.name}
                              </Typography>
                              {table.numberOfVisitors != null && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  icon={<PeopleIcon />}
                                  label={`${table.numberOfVisitors} 名`}
                                />
                              )}
                              <CheckoutRequestChip table={table} />
                              {isAcknowledged && (
                                <Button
                                  size="small"
                                  variant="text"
                                  color="inherit"
                                  onClick={() => setCancelTarget({ activeDiningTableId: table.id, tableName: table.diningTable.name })}
                                  sx={{ minWidth: 0, px: 1, color: 'text.secondary' }}
                                >
                                  取消
                                </Button>
                              )}
                              {/* テーブル移動は稀な操作のため目立たないアイコンのみ（ADR 0040） */}
                              <Tooltip title="テーブル移動">
                                <IconButton
                                  size="small"
                                  sx={{ ml: 'auto', color: 'text.secondary' }}
                                  aria-label={`${table.diningTable.name} をテーブル移動`}
                                  onClick={() =>
                                    setMoveSource({
                                      activeDiningTableId: table.id,
                                      tableName: table.diningTable.name,
                                    })
                                  }
                                >
                                  <SwapIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(table.startTime)}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={2}>
                            <Button
                              variant="contained"
                              color="info"
                              onClick={() =>
                                handleClerkOrder(activeStore.activeStoreId, table.id)
                              }
                              startIcon={<RestaurantIcon />}
                              fullWidth
                            >
                              店員オーダー
                            </Button>
                            {isRequested && (
                              <Button
                                variant="contained"
                                color="warning"
                                onClick={() =>
                                  handleAcknowledgeCheckoutRequest(activeStore.activeStoreId, table.id)
                                }
                                startIcon={<ReceiptIcon />}
                                fullWidth
                              >
                                受領
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              // 会計受付済みは会計が主操作（primary）。それ以外は従来どおり warning。
                              color={isAcknowledged ? 'primary' : 'warning'}
                              onClick={() =>
                                handleBilling(activeStore.activeStoreId, table.id)
                              }
                              startIcon={<PosIcon />}
                              fullWidth
                            >
                              会計
                            </Button>
                          </Stack>
                        </Stack>
                      </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Alert severity="info">稼働中テーブルなし</Alert>
                )}
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RestaurantIcon />
                  非稼働中テーブル
                </Typography>

                <List sx={{ p: 0 }}>
                  {activeStore.inActiveTables.map((table) => (
                    <ListItem
                      key={table.diningTable.id}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        rowGap={1.5}
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ width: '100%' }}
                      >
                        {/* 3等分だと日本語のテーブル名が縦書き状に潰れるため、名前に幅を確保し狭い画面では折り返す */}
                        <Box sx={{ flexGrow: 1, minWidth: 140 }}>
                          <Typography variant="subtitle1" color="primary" fontWeight="bold" noWrap>
                            {table.diningTable.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                          <NumberInput
                            min={1}
                            max={99}
                            defaultValue={1}
                            onChange={(_e: React.SyntheticEvent | null, newValue: number | null) =>
                              handleVisitorChange(table.diningTable.id, Number(newValue))
                            }
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0, ml: 'auto' }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>
                              handleStartDiningTable(
                                activeStore.activeStoreId,
                                table.diningTable.id
                              )
                            }
                            startIcon={<RestaurantIcon />}
                          >
                            開始
                          </Button>
                        </Box>
                      </Stack>
                    </ListItem>
                  ))}
                </List>
              </Box>
              </>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* グリッドモードの人数選択ダイアログ（ADR 0066）。iOS の StartTableSheet と同等。 */}
      <Dialog open={startTarget != null} onClose={() => setStartTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{startTarget?.name} を開始</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            来店人数を選択してください
          </Typography>
          <Stack direction="row" spacing={1} sx={{ my: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <Button
                key={n}
                variant={startVisitors === n ? 'contained' : 'outlined'}
                onClick={() => setStartVisitors(n)}
                sx={{ minWidth: 48 }}
              >
                {n}
              </Button>
            ))}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <NumberInput
              min={1}
              max={99}
              value={startVisitors}
              onChange={(_e: React.SyntheticEvent | null, newValue: number | null) =>
                newValue != null && setStartVisitors(Number(newValue))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartTarget(null)}>キャンセル</Button>
          <Button variant="contained" startIcon={<RestaurantIcon />} onClick={handleStartFromDialog}>
            開始
          </Button>
        </DialogActions>
      </Dialog>

      {activeStore && (
        <TableMoveDialog
          open={moveSource != null}
          fromTableName={moveSource?.tableName ?? ''}
          destinations={activeStore.inActiveTables}
          onClose={() => setMoveSource(null)}
          onMove={handleMoveDiningTable}
        />
      )}

      {/* 会計リクエスト取消の確認（ADR 0068）。誤受領で顧客が注文できなくなったときの戻し手段。 */}
      <ConfirmDialog
        open={cancelTarget != null}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleConfirmCancelCheckoutRequest}
        title="会計リクエストの取消"
        message={`${cancelTarget?.tableName ?? ''} の会計リクエストを取り消しますか？顧客は再び注文できるようになります。`}
        confirmText="取り消す"
        confirmColor="warning"
        closeColor="inherit"
      />

      {isBillModalOpen && billData && selectedDiningTableId && activeStore?.activeStoreId && (
        <BillConfirmModal
          billData={billData}
          storeId={store_id!}
          activeStoreId={activeStore.activeStoreId}
          activeDiningTableId={selectedDiningTableId}
          onClose={() => setIsBillModalOpen(false)}
          paymentMethod={(() => {
            // 会計確認モーダルに顧客の希望支払方法を出す（状態があるときだけ）
            const target = activeStore.activeTables.find((table) => table.id === selectedDiningTableId);
            const checkoutRequest = target?.checkoutRequest;
            return checkoutRequest && checkoutRequest.status !== 'NONE' ? checkoutRequest.paymentMethod : null;
          })()}
        />
      )}
    </Container>
  );
};

export default StoreManagement;
